import { ReactNode, useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Button,
  FileUpload,
  FileUploadArea,
  TextField,
  Tooltip,
  UploadedDocument,
} from '@roc/ui';
import { CustomTaskDialog } from '@roc/feature-general-tasks';
import { observer } from 'mobx-react';
import { Document } from '@roc/feature-types';
import { useStore } from '@roc/client-portal-shared/hooks';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sectionTitle: {
      fontWeight: 700,
      fontSize: '1rem',
      display: 'flex',
      alignItems: 'center',
    },
    proofDescription: {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(2),
    },
  })
);

export interface ResolutionModalProps {
  open: boolean;
  handleClose: () => void;
  document?: Document;
  customContent?: ReactNode;
}

export const ResolutionModal = observer(
  ({ open, handleClose, document, customContent }: ResolutionModalProps) => {
    const classes = useStyles();
    const { resolutionStoreV2, docReviewStoreV2 } = useStore();
    const [comments, setComments] = useState<string>('');
    const [files, setFiles] = useState<FileUpload[]>([]);
    const uploadLocked = docReviewStoreV2.isUploadLocked(document);

    useEffect(() => {
      setComments(document?.description ?? '');
    }, [open]);

    const handleSave = async (overrideFile?: File) => {
      if (document?.taskId) {
        await resolutionStoreV2.submitResolution(
          document?.taskId,
          comments,
          overrideFile ?? files[0]?.file
        );
        setDefaults();
        handleClose();
      }
    };

    const setDefaults = () => {
      setFiles([]);
      setComments('');
    };

    return (
      <>
        {document?.taskId && (
          <CustomTaskDialog
            open={open}
            title={document?.title}
            allowVertScroll={false}
            taskId={document.taskId}
            maxWidth="sm"
            handleClose={handleClose}
            dialogContent={
              <Box>
                {customContent}

                {(document?.dropboxPath || !uploadLocked) && (
                  <>
                    <Typography className={classes.sectionTitle}>
                      Proof of Resolution
                      {document?.taskInformation && (
                        <Tooltip title={document.taskInformation} />
                      )}
                    </Typography>
                    <Typography className={classes.proofDescription}>
                      Supporting documentation required — please attach evidence
                      confirming this lien has been satisfied or released (e.g.,
                      lien release letter, recorded discharge, or payoff
                      confirmation).
                    </Typography>
                  </>
                )}

                <Box mb={2}>
                  <TextField
                    label="Comments"
                    value={comments}
                    onChange={e => setComments(e.target.value)}
                    multiline
                    minRows={4}
                    variant="outlined"
                    fullWidth
                    testId="lien-resolution-comments"
                  />
                </Box>

                {document?.dropboxPath ? (
                  <UploadedDocument
                    document={document}
                    onUpload={uploadLocked ? undefined : file => handleSave(file)}
                  />
                ) : (
                  !uploadLocked && (
                    <FileUploadArea items={files} setItems={setFiles} />
                  )
                )}
              </Box>
            }
            dialogActions={
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSave()}
                testId="lien-resolution-save"
                fullWidth
              >
                SAVE
              </Button>
            }
          />
        )}
      </>
    );
  }
);
